import { toYmd } from '../../utils/datetime';

const DAY_MS = 86400000;
const SPAN_DAYS = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 };
const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The points of the revenue chart: one per calendar day of the period, oldest first.
 *
 * GET /stats/revenue answers only the days that had a sale, so drawing its rows as they come would
 * join two sales weeks apart with a slope (a trend that never happened) and, with a single sale,
 * leave one lone dot. Every day of the window is listed instead, a day without sales at 0.
 *
 * The server groups by UTC calendar day and its window is `period` days back from now, so the
 * window here is counted in UTC days too; a row outside it (a leap year, a browser clock that is
 * behind the server's) widens it rather than vanishing.
 *
 * @param {Array<{ date: string, amount: number|string }>} revenueByDay  result.revenueByDay
 * @param {'7d'|'30d'|'90d'|'1y'} period
 * @param {Date} [now]  the reference time; the browser's clock by default
 * @returns {Array<{ key: string, value: number }>}  `key` is the UTC day as "YYYY-MM-DD"; the chart formats it
 */
export function buildRevenueSeries(revenueByDay, period, now = new Date()) {
  const amounts = new Map();
  (Array.isArray(revenueByDay) ? revenueByDay : []).forEach((row) => {
    const key = String(row?.date || '').slice(0, 10);
    if (DAY_KEY.test(key)) amounts.set(key, (amounts.get(key) || 0) + (Number(row.amount) || 0));
  });

  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  let start = today - (SPAN_DAYS[period] || SPAN_DAYS['30d']) * DAY_MS;
  let end = today;
  amounts.forEach((_, key) => {
    const time = Date.parse(`${key}T00:00:00Z`);
    if (time < start) start = time;
    if (time > end) end = time;
  });

  const points = [];
  for (let time = start; time <= end; time += DAY_MS) {
    const key = toYmd(new Date(time), { timeZone: 'UTC' });
    points.push({ key, value: amounts.get(key) || 0 });
  }
  return points;
}

/** The sum of the points: what the card header says the period earned. */
export function seriesTotal(points) {
  return points.reduce((sum, point) => sum + point.value, 0);
}

/** True when at least one day of the series had revenue (an all-zero chart is an empty state). */
export function hasRevenue(points) {
  return points.some((point) => point.value > 0);
}

/**
 * The revenue axis: a round ceiling and three to five steps of 1, 2 or 5 times a power of ten, so
 * every label is exact ("0, 1 mln, 2 mln, 3 mln"). Recharts' own ticks cut the data maximum into
 * equal parts instead (0, 650 ming, 1.3 mln, then "2 mln" for 1.95 mln, which is a rounded lie), and
 * a 2.5 step would print 1.25 mln as "1.3 mln". The ceiling keeps about 5% of headroom above the
 * highest value, so the line never touches the top edge of the chart.
 *
 * @param {number} max  the highest value of the series
 * @param {number} [intervals]  the most steps to draw
 * @returns {{ top: number, ticks: number[] }}  `top` is the axis ceiling, `ticks` run from 0 to it
 */
export function niceAxis(max, intervals = 5) {
  const peak = Number(max);
  if (!Number.isFinite(peak) || peak <= 0) return { top: 1, ticks: [0, 1] };
  const padded = peak * 1.05;
  const sizes = [];
  for (let power = 0; power <= 12; power += 1) [1, 2, 5].forEach((multiple) => sizes.push(multiple * 10 ** power));
  const step = sizes.find((size) => Math.ceil(padded / size) <= intervals) || padded / intervals;
  const top = Math.ceil(padded / step) * step;
  const ticks = [];
  for (let value = 0; value <= top + step / 2; value += step) ticks.push(Math.round(value * 100) / 100);
  return { top, ticks };
}
